import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from './AuthContext';

const T = {
  bg:'#07080c', bg2:'#0c0f18',
  border:'rgba(255,255,255,.07)',
  white:'#dce6fa', muted:'rgba(220,230,250,.45)', dim:'rgba(220,230,250,.22)',
  cyan:'#00c8f0', blue:'#4d8fff', green:'#2ecc71', red:'#e74c3c',
  sans:'"Syne","DM Sans",system-ui,sans-serif',
};

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('verifying');  // 'verifying' | 'success' | 'error'
  const [message, setMessage] = useState('');
  const { refreshSubscription } = useAuth();

  const token = searchParams.get('token') || '';

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage('No verification token found in the link. Please check your email and try again.');
      return;
    }

    const verify = async () => {
      try {
        const res  = await fetch(`${API}/api/auth/verify-email/`, {
          method:'POST',
          headers:{'Content-Type':'application/json'},
          body: JSON.stringify({ token }),
        });
        const data = await res.json();

        if (!res.ok) {
          setStatus('error');
          setMessage(data.error || 'Verification failed.');
        } else {
          setStatus('success');
          setMessage(data.message || 'Email verified successfully.');
          // Refresh auth state so email_verified flag updates in the app
          try { await refreshSubscription(); } catch {}
        }
      } catch(e) {
        setStatus('error');
        setMessage('Network error. Please try again.');
      }
    };

    verify();
  }, [token]);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800;900&family=DM+Sans:wght@400;500;600&display=swap');
        * { box-sizing:border-box; margin:0; padding:0; }
        @keyframes spin { to{transform:rotate(360deg)} }
        @keyframes pop { 0%{transform:scale(0)}60%{transform:scale(1.15)}100%{transform:scale(1)} }
      `}</style>

      <div style={{
        minHeight:'100vh', background:T.bg, display:'flex',
        alignItems:'center', justifyContent:'center', padding:'24px',
        fontFamily:T.sans,
      }}>
        <div style={{ width:'100%', maxWidth:420, textAlign:'center' }}>

          <div style={{ marginBottom:32 }}>
            <Link to="/" style={{ textDecoration:'none' }}>
              <div style={{ fontSize:22, fontWeight:900, color:T.white, letterSpacing:'-.02em' }}>
                Tylent<span style={{ color:T.cyan }}>ed</span>
              </div>
            </Link>
          </div>

          <div style={{
            background:'rgba(255,255,255,.025)', border:`1px solid ${T.border}`,
            borderRadius:16, padding:'40px 28px',
          }}>
            {status === 'verifying' && (
              <>
                <div style={{ display:'flex', justifyContent:'center', marginBottom:20 }}>
                  <div style={{ width:40, height:40, borderRadius:'50%', border:`3px solid ${T.border}`, borderTopColor:T.cyan, animation:'spin .65s linear infinite' }}/>
                </div>
                <div style={{ fontSize:16, fontWeight:700, color:T.white, marginBottom:8 }}>
                  Verifying your email…
                </div>
                <div style={{ fontSize:13, color:T.muted }}>This only takes a moment.</div>
              </>
            )}

            {status === 'success' && (
              <>
                <div style={{ fontSize:48, marginBottom:16, animation:'pop .4s ease' }}>✅</div>
                <div style={{ fontSize:18, fontWeight:800, color:T.white, marginBottom:8 }}>
                  Email verified!
                </div>
                <div style={{ fontSize:13.5, color:T.muted, lineHeight:1.7, marginBottom:24 }}>
                  Your email address has been confirmed. You now have full access to Tylented.
                </div>
                <Link to="/dashboard" style={{
                  display:'inline-block', padding:'11px 28px', borderRadius:8,
                  background:`linear-gradient(135deg,${T.cyan},${T.blue})`,
                  color:'#07080c', fontSize:14, fontWeight:700, textDecoration:'none',
                }}>Go to Dashboard →</Link>
              </>
            )}

            {status === 'error' && (
              <>
                <div style={{ fontSize:40, marginBottom:16 }}>⚠️</div>
                <div style={{ fontSize:17, fontWeight:800, color:T.white, marginBottom:8 }}>
                  Verification failed
                </div>
                <div style={{
                  fontSize:13.5, color:T.muted, lineHeight:1.7, marginBottom:20,
                  padding:'10px 14px', background:`${T.red}10`, border:`1px solid ${T.red}30`,
                  borderRadius:8,
                }}>{message}</div>
                <div style={{ display:'flex', gap:10, justifyContent:'center', flexWrap:'wrap' }}>
                  <Link to="/login" style={{
                    padding:'9px 18px', borderRadius:8,
                    border:`1px solid ${T.border}`, background:'transparent',
                    color:T.muted, fontSize:13, textDecoration:'none',
                  }}>Back to sign in</Link>
                  <Link to="/dashboard" style={{
                    padding:'9px 18px', borderRadius:8,
                    border:`1px solid ${T.cyan}50`, background:`${T.cyan}12`,
                    color:T.cyan, fontSize:13, fontWeight:600, textDecoration:'none',
                  }}>Request new link</Link>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
